import { useQueryClient } from '@tanstack/react-query';
import { Button } from 'components/atoms';
import WarningPopup from 'components/molecules/popup/warning-popup/WarningPopup';
import { Form } from 'components/organisms';
import {
  musicProductDigitalInitialValues,
  musicProductDigitalTabs,
} from 'components/organisms/forms/music-product-digital/MusicProductDigitalForm';
import { listQueryKeys } from 'constants/queryKey';
import { FormikHelpers } from 'formik';
import { useMutateOptions } from 'hooks';
import { useParamId } from 'hooks/useParamId';
import middleware from 'middleware';
import { useStoreState } from 'providers/StoreStateContext';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PiX } from 'react-icons/pi';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  useGetMusicProductDigital,
  usePutMusicProductDigital,
} from 'services/music-product-digital';
import {
  useDeleteMusicProductDigitalImage,
  usePostMusicProductDigitalImage,
  usePutMusicProductDigitalImage,
} from 'services/music-product-digital/image';
import {
  useDeleteMusicProductDigitalImageThumbnail,
  usePostMusicProductDigitalImageThumbnail,
  usePutMusicProductDigitalImageThumbnail,
} from 'services/music-product-digital/imageThumbnail';
import { usePostMusicProductDigitalSettlementInfo } from 'services/music-product-digital/settlementInfo';
import { IMusicProductDigitalForm } from 'services/music-product-digital/types/form';
import { IMusicProductDigitalResponse } from 'services/music-product-digital/types/query';
import { STATUS } from 'services/types';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { getMainTitle } from 'utils/global/mainTitle.utils';
import { getLastPathname } from 'utils/navbar/getParentPathname';
import { onErrorShowToast } from 'utils/service/errorHandler';

const EditMusicProductDigital = () => {
  const digitalMusicProductId = useParamId('music-product-digital');
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const { musicProductDigitalForm, clearForms } = useStoreState();

  const { data: musicProductDigital, isLoading } = useGetMusicProductDigital(digitalMusicProductId);
  const updateMusicProductDigital = usePutMusicProductDigital();

  const updateSettlementInfo = usePostMusicProductDigitalSettlementInfo();
  const queryClient = useQueryClient();

  const addImage = usePostMusicProductDigitalImage();
  const changeImage = usePutMusicProductDigitalImage();
  const deleteImage = useDeleteMusicProductDigitalImage(digitalMusicProductId);
  const addImageThumbnail = usePostMusicProductDigitalImageThumbnail();
  const changeImageThumbnail = usePutMusicProductDigitalImageThumbnail();
  const deleteImageThumbnail = useDeleteMusicProductDigitalImageThumbnail(digitalMusicProductId);

  const mutateOptions = useMutateOptions<IMusicProductDigitalForm>();

  const musicProductDigitalEditInitialValues = useMemo(() => {
    if (!musicProductDigital) return musicProductDigitalInitialValues;

    if (musicProductDigitalForm)
      return {
        ...musicProductDigitalForm,
        releaseStatus: musicProductDigital.releaseStatus,
      };

    return middleware.musicProductDigital.queryToForm(musicProductDigital);
  }, [musicProductDigitalForm, musicProductDigital]);

  const onEditMusicProductDigital = async (
    values: IMusicProductDigitalForm,
    formikHelpers: FormikHelpers<IMusicProductDigitalForm>,
  ) => {
    let productVersion = values.version;
    if (values.file) {
      const img = new Image();
      img.src = URL.createObjectURL(values.file);

      img.onload = () => {
        console.log('Szerokość:', img.width);
        console.log('Wysokość:', img.height);

        URL.revokeObjectURL(img.src); // zwolnienie pamięci
      };
      musicProductDigitalEditInitialValues.productImageLink?.length
        ? await changeImage.mutateAsync(
            { file: values.file, id: digitalMusicProductId, version: values.version },
            {
              onError: (error) => {
                onErrorShowToast(error, 'toast-message.change-album-cover');
              },
              onSuccess: (data) => {
                productVersion = data.version;
              },
            },
          )
        : await addImage.mutateAsync(
            { file: values.file, id: digitalMusicProductId, version: values.version },
            {
              onError: (error) => {
                onErrorShowToast(error, 'toast-message.add-album-cover');
              },
              onSuccess: (data) => {
                productVersion = data.version;
                formikHelpers.setFieldValue('productImageLink', data.productImageLink);
              },
            },
          );
    }

    if (
      !!musicProductDigitalEditInitialValues.productImageLink?.length &&
      !values.productImageLink?.length &&
      !values.file
    ) {
      await deleteImage.mutateAsync(undefined, {
        onError: (error) => {
          onErrorShowToast(error, 'toast-message.remove-album-cover');
        },
        onSuccess: (data) => {
          productVersion = data.version;
        },
      });
    }

    if (values.thumbnailFile) {
      musicProductDigitalEditInitialValues.thumbnailImageLink?.length
        ? await changeImageThumbnail.mutateAsync(
            { file: values.thumbnailFile, id: digitalMusicProductId, version: productVersion },
            {
              onError: (error) => {
                onErrorShowToast(error, 'toast-message.change-album-cover');
              },
              onSuccess: (data) => {
                productVersion = data.version;
              },
            },
          )
        : await addImageThumbnail.mutateAsync(
            { file: values.thumbnailFile, id: digitalMusicProductId, version: productVersion },
            {
              onError: (error) => {
                onErrorShowToast(error, 'toast-message.add-album-cover');
              },
              onSuccess: (data) => {
                productVersion = data.version;
                formikHelpers.setFieldValue('thumbnailImageLink', data.productImageLink);
              },
            },
          );
    }

    if (
      !!musicProductDigitalEditInitialValues.thumbnailImageLink?.length &&
      !values.thumbnailImageLink?.length &&
      !values.thumbnailFile
    ) {
      await deleteImageThumbnail.mutateAsync(undefined, {
        onError: (error) => {
          onErrorShowToast(error, 'toast-message.remove-album-cover');
        },
        onSuccess: (data) => {
          productVersion = data.version;
        },
      });
    }

    const musicProductDigitalMutation = middleware.musicProductDigital.formToQuery(values);

    await updateMusicProductDigital.mutateAsync(
      { ...musicProductDigitalMutation, digitalMusicProductId, version: productVersion },
      {
        ...mutateOptions(formikHelpers),
        onSuccess: data => goToIndividualMusicProductDigital(data),
      },
    );
  };

  const goToIndividualMusicProductDigital = async (data?: IMusicProductDigitalResponse) => {
    navigate(`/music-product-digital/${data?.id ?? digitalMusicProductId}/${!data || data?.releaseStatus === STATUS.draft ? getLastPathname(pathname) : 'delivery'}`);
    await queryClient.invalidateQueries({
      queryKey: [listQueryKeys.musicProductDigital],
    });
    clearForms();
  };

  return (
    <Form.MusicProductDigital
      i18nextPrefix="music-product-digital"
      onSubmit={onEditMusicProductDigital}
      initialValues={musicProductDigitalEditInitialValues}
      disableTabs={[
        musicProductDigitalTabs.delivery,
        musicProductDigitalTabs.statistics,
        musicProductDigitalTabs.contracts,
      ]}
      tittle={getMainTitle(musicProductDigital?.mainTitle)}
      status={{ isLoading }}
      customBackItem={
        <WarningPopup
          title={t('popup.form-cancelation-title')}
          message={t('popup.form-cancelation-description')}
          onConfirm={() => {
            goToIndividualMusicProductDigital();
          }}
          confirmButtonVariant={BUTTON_VARIANT.background}
          trigger={
            <Button type="button" onlyIcon isClear>
              <Button.Icon icon={<PiX />} />
            </Button>
          }
        />
      }
    />
  );
};

export default EditMusicProductDigital;
